import { ClaimType } from '@app/domain/model/ClaimType';

export const ROUTES = {
    HOME: '/',
    HELP: '/help',

    AUTH: {
        ROOT: '/auth',
        LOGIN: '/auth/login',
        SELECT_CLIENT: '/auth/select-client',
        SUCCESS: '/auth/success',
    },

    SELF_SERVICE: {
        ROOT: '/self-service',
        POLICIES: '/self-service/policies',
        CLAIMS: '/self-service/claims',
        PAYMENTS: '/self-service/payments',
        REQUESTS: '/self-service/requests',
        REQUEST: (id: string) => `/self-service/requests/${id}/request`,
        MESSAGES: '/self-service/messages',

        PROFILE: {
            ROOT: '/self-service/profile',
            UPDATE: '/self-service/profile/update',
            REGISTER: '/self-service/profile/register',
        },

        LIFE_INSURANCE: {
            ROOT: '/self-service/life-insurance',
            DETAIL: (id: string) => `/self-service/life-insurance/${id}`,
            CLAIMS: '/self-service/life-insurance/claims',
            CLAIM_DETAIL: (id: string) => `/self-service/life-insurance/claims/${id}`,
            INVOICES: '/self-service/life-insurance/invoices',
            GROUP_INVOICES: '/self-service/life-insurance/group-invoices',
            GROUP_INVOICE_DETAIL: (id: string) => `/self-service/life-insurance/group-invoices/${id}`,
            PAYMENT_LOYALTY: (id: string) => `/self-service/life-insurance/${id}/payment-loyalty`,
            PAYMENT_HISTORY: (id: string) => `/self-service/life-insurance/${id}/payment-history`,
            INVOICES_HISTORY: (id: string) => `/self-service/life-insurance/${id}/invoices`,
            PAYMENT_FEES: (id: string) => `/self-service/life-insurance/${id}/payment-fees`,
            TERMS_AND_CONDITIONS: (id: string) => `/self-service/life-insurance/${id}/terms-and-conditions`,
            BENEFICIARIES: (id: string) => `/self-service/life-insurance/${id}/beneficiaries`,
            INVESTMENT_TRANSACTION_HISTORY: (id: string) =>
                `/self-service/life-insurance/${id}/investment-transaction-history`,
            PERSON_COVERS: (pageId: string, personId: string) =>
                `/self-service/life-insurance/${pageId}/covers/${personId}`,
        },

        E_HEALTH_INSURANCE: {
            ROOT: '/self-service/e-health-insurance',
            CLAIMS: '/self-service/e-health-insurance/claims',
            CLAIM_DETAIL: (id: string) => `/self-service/e-health-insurance/claims/${id}`,
            ORGANISATIONAL_CLAIM: (date: string, organisation: string) =>
                `/self-service/e-health-insurance/claims/${ClaimType.Organizational}?date=${encodeURIComponent(date)}&organisation=${encodeURIComponent(organisation)}`,
            CARDS_AND_LIMITS: '/self-service/e-health-insurance/cards-and-limits',
            CONSENTS: '/self-service/e-health-insurance/consents',
        },

        SEESAM_HEALTH: {
            ROOT: '/self-service/seesam-health',
            CLAIMS: '/self-service/seesam-health/claims',
            CARDS_AND_LIMITS: '/self-service/seesam-health/cards-and-limits',
        },

        PROPERTY_INSURANCE: {
            ROOT: '/self-service/property-insurance',
            PAYMENT_HISTORY: (id: string) => `/self-service/property-insurance/${id}/payment-history`,
        },
        ACCIDENT_INSURANCE: {
            ROOT: '/self-service/accident-insurance',
            PAYMENT_HISTORY: (id: string) => `/self-service/accident-insurance/${id}/payment-history`,
        },
        TRAVEL_INSURANCE: {
            ROOT: '/self-service/travel-insurance',
            PAYMENT_HISTORY: (id: string) => `/self-service/travel-insurance/${id}/payment-history`,
        },
        TRANSPORT_INSURANCE: {
            ROOT: '/self-service/transport-insurance',
            PAYMENT_HISTORY: (id: string) => `/self-service/transport-insurance/${id}/payment-history`,
        },

        NON_LIFE_INSURANCE: {
            CLAIMS: '/self-service/non-life-insurance/claims',
            CLAIM_DETAIL: (id: string) => `/self-service/non-life-insurance/claims/${id}`,
        },

        CNL_PAYMENTS: {
            ROOT: '/self-service/cnl-payments',
            PENDING_PAYMENTS: '/self-service/cnl-payments/pending-payments',
            SUBSCRIPTIONS: '/self-service/cnl-payments/subscriptions',
        },

        LIFE_PAYMENTS: {
            SUCCESS: '/self-service/payment/cl/success',
            FAILED: '/self-service/payment/cl/failed',
            CANCELLED: '/self-service/payment/cl/cancelled',
        },

        PUBLIC_INVESTMENTS: '/self-service/public-investments',
        RISK_TEST: (policyId?: string) =>
            policyId ? `/self-service/risk-test?policyId=${encodeURIComponent(policyId)}` : '/self-service/risk-test',
        RISK_TEST_RESULT: '/self-service/risk-test/result',

        WIZARDS: {
            CHANGE_AGREEMENT: '/self-service/change-agreement',
            CHANGE_AGREEMENT_SUCCESS: '/self-service/change-agreement/success',
            CHANGE_BENEFICIARY_DEATH: (id: string) => `/self-service/change-beneficiary-death/${id}`,
            CHANGE_BENEFICIARY_SURVIVAL: (id: string) => `/self-service/change-beneficiary-survival/${id}`,
            CHANGE_COVERS: (id: string, personId?: string) =>
                personId
                    ? `/self-service/change-covers/${id}?personId=${encodeURIComponent(personId)}`
                    : `/self-service/change-covers/${id}`,
            CHANGE_INSURED_PERSON: (id: string) => `/self-service/change-insured-person/${id}`,
            CHANGE_INVESTMENT_FUNDS: (id: string) => `/self-service/change-investment-funds/${id}`,
            CHANGE_PAYMENTS: (id: string) => `/self-service/change-payments/${id}`,
            CHANGE_RISK_REVIEW: (id: string) => `/self-service/change-risk-review/${id}`,
            CHANGE_VALIDITY_PERIOD: (id: string) => `/self-service/change-validity-period/${id}`,
            HEALTH_CLAIM: '/self-service/health-claim',
            CNL_HEALTH_CLAIM: '/self-service/cnl-health-claim',
            LIFE_CLAIM: '/self-service/life-claim',
            EMAIL_COLLECTION: (eventId: string, insuredPersonIds: string[], fillerIds: string[], policyId?: string) =>
                `/self-service/forms/email-collection?eventId=${eventId}&ipId=${insuredPersonIds.join('&ipId=')}&fillerId=${fillerIds.join('&fillerId=')}${policyId ? `&policyId=${policyId}` : ''}`,
            TRANSPORT_CLAIM: {
                AUTHENTICATED: {
                    ROOT: '/self-service/transport-claim',
                    INNER_WIZARD: (wizardURL: string) => `/self-service/transport-claim/${wizardURL}`,
                },
                UNAUTHENTICATED: {
                    ROOT: '/claims/transport-claim',
                    INNER_WIZARD: (wizardURL: string) => `/claims/transport-claim/${wizardURL}`,
                },
            },
            PROPERTY_CLAIM: '/self-service/home-claim',
            PERSONAL_ACCIDENT_CLAIM: '/self-service/personal-accident-claim',
            TRAVEL_CLAIM: '/self-service/travel-claim',
            GENERAL_CLAIM: '/self-service/general-claim',
            LIFE_PAYMENT: (policyNumber: string, amount: number, policyId: string, type: Nullable<string> = null) =>
                `/self-service/life-payment?policyNumber=${encodeURIComponent(policyNumber)}&amount=${encodeURIComponent(amount)}${policyId ? `&policyId=${encodeURIComponent(policyId)}` : ''}${type ? `&type=${type}` : ''}`,
            ONCODROP_RENEWAL: {
                ROOT: (id: string) => `/self-service/oncodrop-renewal/${id}`,
                REFUSE_SUCCESS: '/self-service/oncodrop-renewal/refuse-success',
                REFUSE_FAILURE: '/self-service/oncodrop-renewal/refuse-failure',
                CONSULT_SUCCESS: '/self-service/oncodrop-renewal/consult-success',
                CONSULT_FAILURE: '/self-service/oncodrop-renewal/consult-failure',
                PAYMENT_SUCCESS: '/self-service/oncodrop-renewal/payment-success',
                PAYMENT_FAILED: '/self-service/oncodrop-renewal/payment-failed',
            },
        },
    },

    ESALES: {
        TRANSPORT: {
            ROOT: '/esales/transport',
            SUCCESS: '/esales/transport/success',
            CALCULATOR_LT: '/transporto-draudimas-skaiciuokle',
        },
        LIFE: {
            ROOT: '/esales/life',
            SUCCESS: '/esales/life/success',
        },
        TRAVEL: {
            ROOT: '/esales/travel',
            SUCCESS: '/esales/travel/success',
            ALTERNATE: '/keliones-draudimas-skaiciuokle',
        },
        PERSONAL_ACCIDENTS: {
            ROOT: '/esales/personal-accidents',
            SUCCESS: '/esales/personal-accidents/success',
            ALTERNATE: '/nelaimingu-atsitikimu-draudimas-skaiciuokle',
        },
        HOME: {
            ROOT: '/esales/home',
            SUCCESS: '/esales/home/success',
            ALTERNATE: '/turto-draudimas-skaiciuokle',
        },
        FAILURE: '/esales/failure',
    },

    API: {
        AUTH: {
            SWEDBANK: '/api/auth/swedbank',
            SEB: '/api/auth/seb',
        },
    },
};

export const RouteMatchers = {
    isEsalesTransport: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.TRANSPORT.ROOT) || path.includes(ROUTES.ESALES.TRANSPORT.CALCULATOR_LT);
    },

    isEsalesTravel: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.TRAVEL.ROOT) || path.includes(ROUTES.ESALES.TRAVEL.ALTERNATE);
    },

    isEsalesHome: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.HOME.ROOT) || path.includes(ROUTES.ESALES.HOME.ALTERNATE);
    },

    isEsalesLife: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.LIFE.ROOT);
    },

    isEsalesPersonalAccidents: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return (
            path.includes(ROUTES.ESALES.PERSONAL_ACCIDENTS.ROOT) ||
            path.includes(ROUTES.ESALES.PERSONAL_ACCIDENTS.ALTERNATE)
        );
    },

    isEsales: (path: Nullable<string>): boolean => {
        return (
            RouteMatchers.isEsalesTransport(path) ||
            RouteMatchers.isEsalesTravel(path) ||
            RouteMatchers.isEsalesLife(path) ||
            RouteMatchers.isEsalesPersonalAccidents(path) ||
            RouteMatchers.isEsalesHome(path)
        );
    },

    isSelfService: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.SELF_SERVICE.ROOT);
    },

    isAuth: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.AUTH.ROOT);
    },
};
